import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react'
import { api } from '@/lib/api'
import { useAuth } from '@/hooks/use-auth'
import { buildAppHref } from '@/lib/auth'
import { formatDuration, entityRefClass } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export function AdminBaseDataPage() {
  const auth = useAuth()
  const [page, setPage] = useState(1)
  const [pageSize] = useState(50)
  const [baseIdFilter, setBaseIdFilter] = useState('')
  const [linkNoFilter, setLinkNoFilter] = useState('')

  const buildParams = () => {
    const params: Record<string, string> = { page: String(page), page_size: String(pageSize) }
    if (baseIdFilter) params.base_id = baseIdFilter
    if (linkNoFilter) params.link_no = linkNoFilter
    return params
  }

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-base-data', page, pageSize, baseIdFilter, linkNoFilter],
    queryFn: () => api.adminListBaseData(buildParams()),
  })

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0

  return (
    <Card className="bg-card/95 backdrop-blur-sm">
      <CardHeader>
        <CardTitle className="text-2xl">Base Data</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="mb-4 flex flex-wrap items-end gap-3">
          <div>
            <Label className="mb-1 block text-xs text-muted-foreground">Base ID</Label>
            <Input
              type="number"
              placeholder="Filter by base ID"
              className="w-40"
              value={baseIdFilter}
              onChange={(e) => { setBaseIdFilter(e.target.value); setPage(1) }}
            />
          </div>
          <div>
            <Label className="mb-1 block text-xs text-muted-foreground">Link No</Label>
            <Input
              type="number"
              placeholder="Filter by link no"
              className="w-40"
              value={linkNoFilter}
              onChange={(e) => { setLinkNoFilter(e.target.value); setPage(1) }}
            />
          </div>
        </div>

        {isLoading && (
          <div className="py-12 text-center text-muted-foreground">
            <Loader2 className="inline h-5 w-5 animate-spin mr-2" /> Loading base data...
          </div>
        )}

        {error && (
          <div className="rounded-lg border-l-4 border-destructive bg-destructive/10 p-4 text-destructive">
            Error loading base data: {(error as Error).message}
          </div>
        )}

        {data && (
          <>
            <div className="overflow-x-auto rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow className="bg-primary hover:bg-primary">
                    <TableHead className="text-primary-foreground font-semibold">Base ID</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Link No</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Interval</TableHead>
                    <TableHead className="text-primary-foreground font-semibold">Seconds</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground">No base data found</TableCell>
                    </TableRow>
                  ) : (
                    data.items.map((bd, i) => (
                      <TableRow key={`${bd.base_id}-${bd.link_no}-${bd.interval}-${i}`}>
                        <TableCell className="font-medium">{bd.base_id}</TableCell>
                        <TableCell>
                          <Link to={buildAppHref(`/admin/links/${bd.link_no}`, auth)} className={entityRefClass}>{bd.link_no}</Link>
                        </TableCell>
                        <TableCell>{formatDuration(bd.interval)}</TableCell>
                        <TableCell>{formatDuration(bd.seconds)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Page {data.page} of {totalPages} ({data.total} total)
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
